import React from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import * as NavConstants from 'constants/NavConstants';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {getDefaultSearchSearchParams} from 'nav/sidepanel/listing/SearchSearchParams';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {ALL_LISTABLE_TYPES, FilterKey, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';

import {getNavigationURL} from './SearchURLs';

export interface SearchSidepanelListingProps {
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
  activeItemId: string | null;
}

export function SearchSidepanelListing(props: SearchSidepanelListingProps & SearchProps): JSX.Element {
  return (
    <div className="sidepanel">
      <ItemListing
        {...props}
        actionSource={ActionOrigin.SEARCH_SIDEPANEL}
        savedSearchSection={SearchSection.GLOBAL}
        availableTypes={ALL_LISTABLE_TYPES.filter((type) => isModelTypeEnabledForListing(type))}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={(settingsComponents?: JSX.Element) => (
          <SearchSidepanelCrumbs
            activeSavedSearch={props.activeSavedSearch}
            onSelectSavedSearch={props.onSelectSavedSearch}
            onSearch={props.onSearch}
            searchParams={props.searchParams}
            settingsComponents={settingsComponents}
          />
        )}
        extraPrimaryFilterOptions={[{filterKey: FilterKey.ANCESTOR_FOLDER}]}
        availableSortKeys={[
          SortKey.SORT_NAME_KEY,
          SortKey.SORT_IDENTIFIER_KEY,
          SortKey.SORT_MODIFIED_AT_KEY,
          SortKey.SORT_CREATED_AT_KEY,
        ]}
        preservedFilterKeys={[]}
        section={NavConstants.Section.SEARCH}
        dataTestAttrs={{
          component: 'SearchSidepanel',
          element: 'root',
          key: props.isExpanded ? 'expanded' : 'collapsed',
        }}
      />
    </div>
  );
}

export function SearchSidepanelCrumbs(props: {
  activeSavedSearch?: SavedSearch;
  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
  settingsComponents?: JSX.Element;
}): JSX.Element {
  if (props.activeSavedSearch) {
    return (
      <SplitBreadcrumbs
        crumbs={[
          <CrumbLink
            key="Search"
            text="Search"
            onClick={() => {
              props.onSearch(
                SearchUtil.clearSearchParams(props.searchParams, [], getDefaultSearchSearchParams().filters)
              );
              props.onSelectSavedSearch(undefined);
            }}
          />,
          <CrumbLink key="root" text={props.activeSavedSearch.name} />,
        ]}
        settingsIcon={props.settingsComponents}
      />
    );
  }

  return (
    <SplitBreadcrumbs
      crumbs={[<CrumbLink key="root" text="Search" />]}
      settingsIcon={props.settingsComponents}
    />
  );
}

function getSearchURL(
  prevProps: SearchSidepanelListingProps & SearchProps,
  props: SearchSidepanelListingProps & SearchProps
): NavigationUpdate | null {
  if (!props.isExpanded) {
    return null;
  }
  return {
    url: getNavigationURL('/search', props.searchParams, props.filterErrors),
    options: {replace: prevProps.isExpanded === props.isExpanded},
  };
}

export default withSearching(SearchSidepanelListing, SearchType.GLOBAL, NavConstants.SECTIONS.SEARCH, {
  defaultSearchParams: getDefaultSearchSearchParams(),
  getNavigationUpdate: getSearchURL,
});
